import {Injectable, Injector} from '@angular/core'
import {BehaviorSubject, catchError, filter, map, Observable, of, take} from "rxjs"
import {UserService} from "app/core/user/user.service";
import {cloneDeep} from "lodash-es";
import {environment} from "environments/environment";
import { HttpClient } from "@angular/common/http";
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";
import {IUser} from 'app/interfaces/IUser'
import {EventMessagesService, eventType} from "@store/event-messages.service";
import Bugsnag from '@bugsnag/js'
import {AppModeService} from "@store/services/app-mode.service";
import {AppMode} from "../../../enums/AppModeEnum";
import {SurveyViewerService} from "../../survey/survey-viewer/services/survey-viewer.service";
import {ViewerTypeEnum} from "app/enums/ViewerTypeEnum";
import {Question} from "app/models/questions/Question";
import {PublicationAccessService} from "../publication-access.service";
import {AUDIO_RECORDER_EXT_PROFILE_KEY, cloneAudioRecorderExtProfileDefinition} from "./constants/audio-recorder-ext-profile";
import Query from 'devextreme/data/query';

export interface extStorage {
  extDef: {},
  ask: string[],
  neverAsk: string[],
  randomAssign: string[]
}

export interface askExtStatus {
  [key: string]: {
    exist: boolean;
    pass: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ExtProfilesService {

  BASE_URL = environment.apiUrl;

  private _localStorage: extStorage = {
    extDef: {},
    ask: [],
    neverAsk: [],
    randomAssign: []
  }

  public extDef: any;

  private lastCollectionStatus = false;
  collectStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  extProfilesToAsk$: BehaviorSubject<askExtStatus> = new BehaviorSubject<askExtStatus>({});
  randomAssign$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  unAnsweredIndex$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  extProfilesToAskIndex$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  nextToAsk$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  hasPendingRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  private audioRequirementChecked = false;
  private audioRequirementPresent = false;
  private audioQuestionInjected = false;
  private audioNegativeAnswerPending = false;
  private audioPositivePending = false;
  private audioPositiveTestInProgress = false;
  private audioScreenOutTriggered = false;
  audioPositiveTestInProgress$ = new BehaviorSubject<boolean>(false);

  constructor(
    private injector: Injector,
    private userService: UserService,
    private _httpClient: HttpClient,
    private toastrService: ToastrService,
    private translate: TranslocoService,
    private eventMessagesService: EventMessagesService,
    private appModeService: AppModeService,
  ) {

    this.userService.user$.subscribe((aUser) => {
      this.calculateToAsk();
    });

  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------
  set(values: extStorage = null, alreadyAnswered = []) {

    try {

      if (values.ask?.length === 0) {
        this.collectStatus$.next(true);
      }

      this._localStorage = values;
      this.extDef = values.extDef;
      this.audioRequirementChecked = false;
      this.audioRequirementPresent = false;

      const extPToAsk: askExtStatus = {}

      values.ask.forEach((anE) => {

        extPToAsk[anE] = {
          pass: alreadyAnswered.includes(anE),
          exist: alreadyAnswered.includes(anE),
        };

        if (!values.extDef[anE] && anE !== AUDIO_RECORDER_EXT_PROFILE_KEY) {
          this.toastrService.warning(
            `${this.translate.translate('L\'extProfile')}<a href="${environment.clientAppUrl}/projects/extprofils" target="_blank"><pre>${anE}</pre></a>${this.translate.translate('est introuvable dans la bibliothèque.')}<br>${this.translate.translate('Vérifier qu\'il existe bien dans les extProfliles.')}`,
            anE,
            {
              timeOut: 10000,
              progressBar: true,
              enableHtml: true,
            }
          )
        }

      })

      this.extProfilesToAsk$.next(extPToAsk);
      this.calculateToAsk();
      this.audioRequirementChecked = false;
      this.audioRequirementPresent = false;
      this.audioQuestionInjected = false;
      this.audioNegativeAnswerPending = false;
      this.audioPositivePending = false;
      this.audioPositiveTestInProgress = false;
      this.audioScreenOutTriggered = false;
      this.audioRulesChecked = false; // Réinitialiser le flag pour permettre la vérification des règles lors du prochain calculateToAsk()
      this.audioPositiveTestInProgress$.next(false);
      this.ensureAudioRequirement().catch(() => undefined);

    } catch (e) {
      console.error('[ext-profiles.service] Error setting exts', e);
    }

  }

  setExtDef(extDef: any) {
    this.extDef = extDef;
    this.audioRequirementChecked = false;
    this.audioRequirementPresent = false;
    this.audioQuestionInjected = false;
    this.audioNegativeAnswerPending = false;
    this.audioPositivePending = false;
    this.audioPositiveTestInProgress = false;
    this.audioScreenOutTriggered = false;
    this.audioPositiveTestInProgress$.next(false);
    this.ensureAudioRequirement().catch(() => undefined);
  }

  setAll(exts: string[] = []) {

    try {
      const init = {};
      exts?.forEach((key) => {
        init[key] = '';
      });
      this.updateMultipleExtProfiles(init);
      this.audioRequirementChecked = false;
      this.audioRequirementPresent = false;
      this.audioQuestionInjected = false;
      this.audioNegativeAnswerPending = false;
      this.audioPositivePending = false;
      this.audioPositiveTestInProgress = false;
      this.audioScreenOutTriggered = false;
      this.audioPositiveTestInProgress$.next(false);
      this.ensureAudioRequirement().catch(() => undefined);
    } catch (e) {
      console.error('Error setting all  exts', e);
    }

  }

  getAsk(): string[] {
    return this._localStorage.ask;
  }

  getRandomAssign(): string[] {
    return this._localStorage.randomAssign;
  }

  getNeverAsk(): string[] {
    return this._localStorage.neverAsk;
  }

  extHasParams(keyName: string): boolean {
    return !!this.extDef?.[keyName];
  }

  getParams(keyName: string): any[] {
    const selectedExt = this.extDef?.[keyName];
    if (
      selectedExt &&
      selectedExt.data &&
      selectedExt.data.params
    ) {
      return selectedExt.data.params
    }
    return [];

  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  private injectAudioQuestion(): void {
    if (this.audioQuestionInjected) {
      return;
    }

    // On injecte toujours la question audio, la vérification des règles
    // se fera dans nextExt() juste avant de l'afficher
    if (!this.extDef) {
      this.extDef = {};
    }

    if (!this.extDef[AUDIO_RECORDER_EXT_PROFILE_KEY]) {
      this.extDef[AUDIO_RECORDER_EXT_PROFILE_KEY] = cloneAudioRecorderExtProfileDefinition();
    }

    // Injecte la clé en première position dans la liste des EXT à poser.
    const askList = this._localStorage?.ask || [];
    const filteredAsk = askList.filter((key) => key !== AUDIO_RECORDER_EXT_PROFILE_KEY);
    this._localStorage.ask = [AUDIO_RECORDER_EXT_PROFILE_KEY, ...filteredAsk];

    const extProfilesToAsk = cloneDeep(this.extProfilesToAsk$.value || {});
    extProfilesToAsk[AUDIO_RECORDER_EXT_PROFILE_KEY] = extProfilesToAsk[AUDIO_RECORDER_EXT_PROFILE_KEY] || {
      exist: false,
      pass: false,
    };
    this.extProfilesToAsk$.next(extProfilesToAsk);

    const indexList = this.extProfilesToAskIndex$.value || [];
    const filteredIndex = indexList.filter((key) => key !== AUDIO_RECORDER_EXT_PROFILE_KEY);
    this.extProfilesToAskIndex$.next([AUDIO_RECORDER_EXT_PROFILE_KEY, ...filteredIndex]);

    this.audioQuestionInjected = true;
    this.calculateToAsk();
  }

  /**
   * Update and calculated needed values.
   *
   */
  getExtProfiles() {
    return this.userService.user.extProfiles;
  }

  updateExtProfiles(key: string, value: string) {

    let nUser: IUser = cloneDeep(this.userService.user);

    nUser = {
      ...nUser,
      extProfiles: {
        ...nUser?.extProfiles || {},
        [key]: value,
      }
    }

    //
    // Post traitement ?
    //
    this.userService.user$.next(nUser);
    this.postUpdateExt(key, value);

  }

  persistUserExtProfiles(key, value): Observable<boolean> {

    this.hasPendingRequest$.next(true)

    if (this.userService.user$.value.isPublic || !value || this.appModeService.isTestSurvey$.value) {
      this.hasPendingRequest$.next(false)
      return of(false)
    }

    const list = [
      {
        keyName: key,
        keyValue: value
      }
    ]

    const endpoint = `${this.BASE_URL}/user/setExtprofils`
    this._httpClient.post(endpoint, {list: list})
      .subscribe({
        next: () => {
          this.hasPendingRequest$.next(false)
        },
        error: err => {
          this.hasPendingRequest$.next(false)
          Bugsnag.notify(err, (event) => {
            event.addMetadata('Ext Profiles Service', {
              method: this.persistUserExtProfiles.name,
              body: list,
              endpoint
            })
          })
        }
      })
    return of(true)
  }

  updateMultipleExtProfiles(exts: Record<string, string>) {

    let nUser: IUser = cloneDeep(this.userService.user);

    nUser = {
      ...nUser,
      extProfiles: {
        ...nUser?.extProfiles || {},
        ...exts,
      }
    }

    //
    // Missing Logs....
    //
    // Object.keys(exts).forEach((aKey) => {
    //   if (exts[aKey]?.length > 0) {
    //     this.eventMessagesService.emitEvent(eventType.extUpdateValue, {key: aKey, value: exts[aKey]})
    //   }
    // })

    // ?
    this.userService.user$.next(nUser);
    Object.keys(exts).forEach((key) => {
      this.postUpdateExt(key, exts[key]);
    })

    // this.checkIfAllExtCollected();

  }

  clearExtProfiles() {

    const extKeys = Object.keys(this.userService.user.extProfiles);
    const clearedKeys = {};
    extKeys.forEach((aKey) => {
      clearedKeys[aKey] = '';
    })

    this.userService.user$.next({
        ...this.userService.user,
        extProfiles: clearedKeys,
      }
    )
  }

  nextAll() {

    const newExtProfilesToAskList: askExtStatus = cloneDeep(this.extProfilesToAsk$.value);
    Object.keys(newExtProfilesToAskList).forEach((anExt) => {
      newExtProfilesToAskList[anExt] = {
        ...newExtProfilesToAskList[anExt],
        exist: true,
        pass: true,
      };
    });
    this.extProfilesToAsk$.next(newExtProfilesToAskList);
    this.extProfilesToAskIndex$.next(Object.keys(newExtProfilesToAskList)); // <--- pas que les pas répondus...

  }

  removeExtProfile(key: string) {

    let extProfiles = cloneDeep(this.userService.user.extProfiles);
    delete extProfiles[key];
    this.userService.user$.next({
      ...this.userService.user,
      extProfiles,
    });

  }

  doGetGeoIpInfos() {
    const action = 'getGeoIpLocation'
    const endpoint = `${this.BASE_URL}/4dm1n/publicActions`
    const body = {
      action,
      params: {},
    }

    return this._httpClient.post(endpoint, body)
      .pipe(
        map((response: any) => {
          this.updateMultipleExtProfiles({
            'internal_country': response.data.datas.country.isoCode,
            'address_components_city': response.data.datas.city.name,
          });

          this.userService.setLocalization(response.data.datas);

          return response;

        }),
        catchError(err => {
          Bugsnag.notify(err, (event) => {
            event.addMetadata('Ext Profiles Service', {
              method: this.doGetGeoIpInfos.name,
              body,
              endpoint
            })
          })

          return of(false)
        })
      );
  }

  randomAssign(ext: string[]) {

    ext.forEach((key) => {

      if (this.extDef[key]) {
        const randomIndex = Math.floor(Math.random() * this.extDef[key].data.params.length);
        const value = this.extDef[key].data['params'][randomIndex]['key']
        this.updateExtProfiles(key, `${value}`);

      }

    })

  }


  /**
   * Assign values to determine what has been replied or not...
   * --> [] => IExt object
   */
  nextExt(ext: string) {
    if (this.audioScreenOutTriggered || this.audioPositiveTestInProgress) {
      return;
    }

    if (ext === AUDIO_RECORDER_EXT_PROFILE_KEY) {
      // TOUJOURS vérifier les règles AVANT de demander le micro ou d'afficher la question
      const willShowAudioQuestion = this.checkAudioQuestionRules();
      
      if (!willShowAudioQuestion) {
        // Les règles indiquent que la question audio ne sera pas visible
        // → marquer comme passée sans afficher la question ni demander le micro
        // Réinitialiser les états audio si la question ne sera pas visible
        this.audioPositivePending = false;
        this.audioNegativeAnswerPending = false;
        this.markExtAsPassed(ext);
        return;
      }

      // Les règles indiquent que la question sera visible
      if (this.audioNegativeAnswerPending) {
        this.handleAudioNegativeAnswer();
        return;
      }

      if (this.audioPositivePending) {
        // L'utilisateur a répondu "oui" à la question audio ET les règles indiquent qu'elle sera visible
        // → vérifier l'accès au micro
        this.audioPositiveTestInProgress = true;
        this.audioPositiveTestInProgress$.next(true);
        this.verifyMicrophoneAccess()
          .then(() => {
            this.audioPositivePending = false;
            this.audioPositiveTestInProgress = false;
            this.audioPositiveTestInProgress$.next(false);
            this.markExtAsPassed(ext);
          })
          .catch((error) => {
            console.warn('[ExtProfilesService] Audio microphone test failed', error);
            this.audioPositivePending = false;
            this.audioPositiveTestInProgress = false;
            this.audioPositiveTestInProgress$.next(false);
            this.handleAudioRequirementFailure();
          });
        return;
      }

      // Les règles sont respectées → la question sera affichée normalement
      // Le composant affichera la question et l'utilisateur pourra répondre
      // Si "oui" → audioPositivePending sera true et on vérifiera l'accès au micro
      // Si "non" → audioNegativeAnswerPending sera true et on fera un screenout
      return;
    }

    this.markExtAsPassed(ext);
  }

  /**
   * Update state to know what to ask next and what has been asked
   */
  private calculateToAsk() {

    const aUser: IUser = this.userService.user;

    if (Object.keys(this.extProfilesToAsk$.value).length > 0) {

      const userExtFilled = Object.keys(aUser?.extProfiles || {});

      const newExtProfilesToAskList: askExtStatus = cloneDeep(this.extProfilesToAsk$.value);
      const next = [];

      const unAnsweredIndex = [];

      // Vérifier les règles audio DÈS LE DÉBUT, avant de construire la liste des questions à afficher
      // Si la question audio existe dans la liste mais que les règles indiquent qu'elle ne sera pas visible,
      // on la marque comme passée immédiatement pour éviter de l'afficher
      let audioQuestionShouldBeSkipped = false;
      if (newExtProfilesToAskList[AUDIO_RECORDER_EXT_PROFILE_KEY] && !this.audioRulesChecked) {
        const willShowAudioQuestion = this.checkAudioQuestionRules();
        this.audioRulesChecked = true; // Marquer comme vérifié pour ne pas refaire
        
        if (!willShowAudioQuestion) {
          // Les règles indiquent que la question ne sera pas visible → la marquer comme passée
          audioQuestionShouldBeSkipped = true;
          newExtProfilesToAskList[AUDIO_RECORDER_EXT_PROFILE_KEY] = {
            ...newExtProfilesToAskList[AUDIO_RECORDER_EXT_PROFILE_KEY],
            exist: true,
            pass: true,
          };
        }
      }

      Object.keys(newExtProfilesToAskList).forEach((anExt) => {
        // Si c'est la question audio et qu'elle doit être sautée (non visible), on préserve son état pass: true
        if (anExt === AUDIO_RECORDER_EXT_PROFILE_KEY && audioQuestionShouldBeSkipped) {
          // Ne pas modifier la question audio si elle doit être sautée
          return;
        }
        
        if (!userExtFilled.includes(anExt)) {
          newExtProfilesToAskList[anExt] = {
            ...newExtProfilesToAskList[anExt],
            exist: false,
            pass: false,
          };
          unAnsweredIndex.push(anExt);
        } else {
          newExtProfilesToAskList[anExt] = {
            ...newExtProfilesToAskList[anExt],
            exist: true,
          };

        }

        //
        //
        //
        if (!newExtProfilesToAskList[anExt].exist && !newExtProfilesToAskList[anExt].pass) {
          next.push(anExt);
        }

      })

      this.nextToAsk$.next(next[0]);
      this.extProfilesToAsk$.next(newExtProfilesToAskList);
      this.extProfilesToAskIndex$.next(Object.keys(newExtProfilesToAskList)); // <--- pas que les pas répondus...
      this.unAnsweredIndex$.next(unAnsweredIndex);

      this.checkIfAllExtCollected();

    }

  }

  /**
   * Vérifie si la question audio doit être affichée selon les règles
   * Cette méthode est appelée depuis le composant au moment de l'affichage
   */
  public shouldShowAudioQuestion(): boolean {
    return this.checkAudioQuestionRules();
  }

  private audioRulesChecked = false; // Flag pour s'assurer que la vérification ne se fait qu'une fois

  public checkIfAllExtCollected() {

    let collectOk = true;
    if (this.audioScreenOutTriggered) {
      return;
    }
    const extCollected = cloneDeep(this.extProfilesToAsk$.value);

    // Vérifier s'il reste des questions ext-profile normales (non audio) à répondre
    let hasUnansweredNonAudioExt = false;
    Object.keys(extCollected).forEach((anExt) => {
      if (anExt !== AUDIO_RECORDER_EXT_PROFILE_KEY) {
        if (!extCollected[anExt].exist || !extCollected[anExt].pass) {
          hasUnansweredNonAudioExt = true;
          collectOk = false;
        }
      }
    });

    // Note: La vérification des règles audio se fait maintenant dans calculateToAsk() au début
    // pour éviter d'afficher la question ext-profile audio si elle ne doit pas être visible

    // Vérifier maintenant si toutes les questions (y compris audio si elle doit être affichée) sont terminées
    Object.keys(extCollected).forEach((anExt) => {
      // Si c'est la question audio, vérifier les règles pour voir si elle doit être considérée comme passée
      if (anExt === AUDIO_RECORDER_EXT_PROFILE_KEY) {
        const willShowAudioQuestion = this.checkAudioQuestionRules();
        // Si la question audio ne doit pas être affichée selon les règles, on la considère comme passée
        if (!willShowAudioQuestion) {
          return; // Ne pas bloquer la finalisation
        }
      }
      
      if (!extCollected[anExt].exist || !extCollected[anExt].pass) {
        collectOk = false;
      }
    });

    // Si on en a pas ... on met à True!!
    if (Object.keys(this.extProfilesToAsk$.value).length === 0) {
      collectOk = true;
    }

    if (collectOk) {
      this.finalizeExtCollection();
      return;
    }

    if (this.lastCollectionStatus !== collectOk) {
      this.collectStatus$.next(collectOk);
      this.lastCollectionStatus = collectOk;
    }

  }

  setupRandom(){
    this.randomAssign$.getValue().forEach((key) => {
      if (this.extDef[key]) {
        const randomIndex = Math.floor(Math.random() * this.extDef[key].data.params.length);
        const value = this.extDef[key].data['params'][randomIndex]['key']
        this.updateExtProfiles(key, `${value}`);
      }
    });
    this.randomAssign$.next([]);
  }

  private postUpdateExt(keyName: string, value: string) {

    if (value?.length > 0 && !this.appModeService.isTestSurvey$.value) {
      this.eventMessagesService.emitEvent(eventType.extUpdateValue, {keyName, value});
    }

    this.persistUserExtProfiles(keyName, value)

    switch (keyName) {
      case 'internal_birthdate':
        const age = `${new Date().getFullYear() - parseInt(value, 0)}`;
        if (!isNaN(Number(age))) {
          this.updateExtProfiles('internal_age', age);
          this.updateExtProfiles('calculated_age', age);
          this.decodeAgeRange(age);
        }

        break;

      case AUDIO_RECORDER_EXT_PROFILE_KEY:
        this.handleAudioQuestionAnswer(value);
        break;

      // case 'internal_age':
      //     break;

      default:
        // On fait rien...
        return of(true);
    }


  }

  private decodeAgeRange(age) {

    // sql += '\n     IF(calculatedAge.age < 20, \'< 20\',';
    // sql += '\n        IF(calculatedAge.age BETWEEN 20 AND 29, \'20 - 29\',';
    // sql += '\n           IF(calculatedAge.age BETWEEN 30 AND 39, \'30 - 39\',';
    // sql += '\n              IF(calculatedAge.age BETWEEN 40 AND 49, \'40 - 49\',';
    // sql += '\n                 IF(calculatedAge.age BETWEEN 50 AND 59, \'50 - 59\',';
    // sql += '\n                    IF(calculatedAge.age BETWEEN 60 AND 69, \'60 - 69\', \'> 70\')))))) key_value';

    age = parseInt(`${age}`, 0);

    let value = '< 20';

    if (age < 20) {
      value = '< 20';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 20 && age <= 29) {
      value = '20 - 29';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 30 && age <= 39) {
      value = '30 - 39';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 40 && age <= 49) {
      value = '40 - 49';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 50 && age <= 59) {
      value = '50 - 59';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 60 && age <= 69) {
      value = '60 - 69';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 70) {
      // value = '> 69';
      value = '70';
      return this.updateExtProfiles('age_range', value);
    }

  }

  private handleAudioQuestionAnswer(value: string): void {
    if (!value) {
      return;
    }

    const normalized = value.trim().toLowerCase();
    const negativeAnswer = normalized.includes('non_');

    if (negativeAnswer) {
      this.audioNegativeAnswerPending = true;
      this.audioPositivePending = false;
      return;
    }

    this.audioNegativeAnswerPending = false;
    this.audioPositivePending = true;

    // Update ext state to show answer but keep pass=false until mic test succeeds
    const extState = this.extProfilesToAsk$.value || {};
    if (extState[AUDIO_RECORDER_EXT_PROFILE_KEY]) {
      extState[AUDIO_RECORDER_EXT_PROFILE_KEY] = {
        exist: true,
        pass: false,
      };
      this.extProfilesToAsk$.next(extState);
      this.calculateToAsk();
    }
  }


  private detectAudioRecorderInLoadedSurvey(): boolean | null {
    try {
      const surveyViewerService = this.injector.get(SurveyViewerService, null);

      if (!surveyViewerService) {
        return null;
      }

      const activeSurvey = surveyViewerService.activeSurvey$?.value;

      if (activeSurvey?.questions) {
        const hasAudio = Object.values(activeSurvey.questions).some((question: Question) => this.isAudioRecorderViewer(question?.viewerType));
        if (hasAudio) {
          this.audioRequirementChecked = true;
          this.audioRequirementPresent = true;
        }
        return hasAudio;
      }

      const rawSurvey = surveyViewerService.initialRawSurvey ?? surveyViewerService.initialRawSurvey$?.value;
      const rawQuestions = rawSurvey?.surveyDef?.questions ?? rawSurvey?.questions;

      if (rawQuestions) {
        const hasAudio = Object.values(rawQuestions).some((question: any) => this.isAudioRecorderViewer(question?.viewerType));
        if (hasAudio) {
          this.audioRequirementChecked = true;
          this.audioRequirementPresent = true;
        }
        return hasAudio;
      }

    } catch (error) {
      console.warn('[ExtProfilesService] Impossible de déterminer la présence d\'une question audioRecorder avec le sondage chargé', error);
    }

    return null;
  }

  private finalizeExtCollection(): void {
    this.lastCollectionStatus = true;
    this.collectStatus$.next(true);
    this.appModeService.appMode$.next(AppMode.HOME);
    this.eventMessagesService.emitEvent(eventType.extAllCollected, {values: this.userService.user.extProfiles});
  }

  private isAudioRecorderViewer(viewerType: any): boolean {
    if (!viewerType) {
      return false;
    }
    const parsedType = `${viewerType}`.toLowerCase();
    return parsedType === ViewerTypeEnum.AUDIORECORDER.toLowerCase();
  }

  private checkAudioRequirementFromExt(): boolean {
    const askList = this._localStorage?.ask || [];
    const extProfilesToAsk = this.extProfilesToAsk$.value || {};
    const extDef = this.extDef || {};
    const contributorExts = this.extProfilesToAskIndex$.value || [];
    const randomAssign = this.randomAssign$.value || [];

    return askList.includes(AUDIO_RECORDER_EXT_PROFILE_KEY) ||
      contributorExts.includes(AUDIO_RECORDER_EXT_PROFILE_KEY) ||
      randomAssign.includes(AUDIO_RECORDER_EXT_PROFILE_KEY) ||
      typeof extProfilesToAsk[AUDIO_RECORDER_EXT_PROFILE_KEY] !== 'undefined' ||
      !!extDef[AUDIO_RECORDER_EXT_PROFILE_KEY];
  }

  private async ensureAudioRequirement(): Promise<boolean> {
    if (this.audioRequirementPresent) {
      this.injectAudioQuestion();
      return true;
    }

    if (this.checkAudioRequirementFromExt()) {
      this.audioRequirementPresent = true;
      this.audioRequirementChecked = true;
      this.injectAudioQuestion();
      return true;
    }

    if (this.audioRequirementChecked) {
      return false;
    }

    const loadedSurveyResult = this.detectAudioRecorderInLoadedSurvey();
    if (loadedSurveyResult !== null) {
      this.audioRequirementChecked = true;
      this.audioRequirementPresent = loadedSurveyResult;
      if (loadedSurveyResult) {
        this.injectAudioQuestion();
      }
      return loadedSurveyResult;
    }

    const hasAudio = await this.waitForSurveyIdsAndDetectAudio();
    this.audioRequirementChecked = true;
    this.audioRequirementPresent = hasAudio;
    if (hasAudio) {
      this.injectAudioQuestion();
    }
    return hasAudio;
  }

  private waitForSurveyIdsAndDetectAudio(): Promise<boolean> {
    const publicationAccessService = this.injector.get(PublicationAccessService, null);
    const surveyIdsSubject = publicationAccessService?.allSurveyIds$;
    const surveyIds = surveyIdsSubject?.value || [];

    if (!surveyIds?.length) {
      if (!surveyIdsSubject) {
        return Promise.resolve(false);
      }

      return new Promise((resolve) => {
        const subscription = surveyIdsSubject
          .pipe(
            filter((ids) => ids?.length > 0),
            take(1)
          )
          .subscribe({
            next: (ids) => {
              subscription.unsubscribe();
              this.fetchSurveyAndDetectAudio(ids?.[0]).then(resolve);
            },
            error: () => {
              subscription.unsubscribe();
              resolve(false);
            }
          });
      });
    }

    return this.fetchSurveyAndDetectAudio(surveyIds[0]);
  }

  private fetchSurveyAndDetectAudio(surveyId?: number): Promise<boolean> {
    if (!surveyId) {
      return Promise.resolve(false);
    }

    const endpoint = `${this.BASE_URL}/4dm1n/publicActions`;
    const payload = {
      action: 'getSurvey',
      params: {
        surveyId: Number(surveyId),
        userId: this.userService.user?.id,
      }
    };

    return new Promise((resolve) => {
      this._httpClient.post<{ data?: { datas?: any } }>(endpoint, payload)
        .pipe(
          map((response) => response?.data?.datas ?? null),
          catchError((error) => {
            console.warn('[ExtProfilesService] Impossible de récupérer le sondage pour vérifier la présence d\'une question audioRecorder', error);
            return of(null);
          })
        )
        .subscribe((survey) => {
          if (!survey) {
            resolve(false);
            return;
          }

          const rawQuestions = survey?.surveyDef?.questions ?? survey?.questions;

          if (!rawQuestions) {
            resolve(false);
            return;
          }

          const hasAudio = Object.values(rawQuestions).some((question: any) =>
            this.isAudioRecorderViewer(question?.viewerType) ||
            this.isAudioRecorderViewer(question?.type)
          );

          resolve(hasAudio);
        });
    });
  }

  /**
   * Handle screenout when user explicitly answers "no" to audio question
   * Uses normal screenout without audio-specific message
   */
  private handleAudioNegativeAnswer(): void {
    // User explicitly answered "no" to the audio question - use normal screenout
    this.collectStatus$.next(false);
    this.lastCollectionStatus = false;
    this.audioRequirementPresent = false;
    this.audioRequirementChecked = false;
    this.audioQuestionInjected = false;
    this.audioNegativeAnswerPending = false;
    this.audioPositivePending = false;
    this.audioPositiveTestInProgress = false;
    this.audioScreenOutTriggered = true;
    this.audioPositiveTestInProgress$.next(false);

    this.extProfilesToAsk$.next({});
    this.extProfilesToAskIndex$.next([]);
    this.unAnsweredIndex$.next([]);
    this.nextToAsk$.next(null);

    const publicationAccessService = this.injector.get(PublicationAccessService, null) as any;

    if (publicationAccessService && typeof publicationAccessService.doNormalScreenOut === 'function') {
      publicationAccessService.doNormalScreenOut();
      return;
    }

    // Fallback if PublicationAccessService is not available
    this.eventMessagesService.emitEvent(eventType.landingScreenOut, this.getExtProfiles());
  }

  private handleAudioRequirementFailure(): void {
    // Microphone access denied or unavailable - use audio-specific screenout
    this.collectStatus$.next(false);
    this.lastCollectionStatus = false;
    this.audioRequirementPresent = false;
    this.audioRequirementChecked = false;
    this.audioQuestionInjected = false;
    this.audioNegativeAnswerPending = false;
    this.audioPositivePending = false;
    this.audioPositiveTestInProgress = false;
    this.audioScreenOutTriggered = true;
    this.audioPositiveTestInProgress$.next(false);


    this.extProfilesToAsk$.next({});
    this.extProfilesToAskIndex$.next([]);
    this.unAnsweredIndex$.next([]);
    this.nextToAsk$.next(null);

    this.screenOutForAudioRequirement();
  }

  private screenOutForAudioRequirement(): void {
    const publicationAccessService = this.injector.get(PublicationAccessService, null) as any;

    if (publicationAccessService && typeof publicationAccessService.screenOutForAudioRequirement === 'function') {
      publicationAccessService.screenOutForAudioRequirement();
      return;
    }

    this.eventMessagesService.emitEvent(eventType.landingScreenOut, {reason: 'microphone_not_authorized'});
  }

  public markExtAsPassed(ext: string): void {
    const newExtProfilesToAskList = cloneDeep(this.extProfilesToAsk$.value);
    if (newExtProfilesToAskList[ext]) {
      newExtProfilesToAskList[ext] = {
        ...newExtProfilesToAskList[ext],
        exist: true,
        pass: true,
      };
    }
    this.extProfilesToAsk$.next(newExtProfilesToAskList);
    this.calculateToAsk();
  }

  /**
   * Convertit les objets avec clés numériques (stockés par Firebase) en tableaux
   * pour que Query().filter() puisse les utiliser
   * Exemple: {0: {0: "internal_gender", 1: "=", 2: "F"}} -> [["internal_gender", "=", "F"]]
   */
  private convertObjectsToArrays(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    // Si c'est déjà un tableau, le traiter récursivement
    if (Array.isArray(obj)) {
      return obj.map(item => this.convertObjectsToArrays(item));
    }
    
    // Si c'est un objet
    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      
      // Si toutes les clés sont numériques (0, 1, 2, ...), c'est probablement un tableau converti en objet
      const allNumericKeys = keys.length > 0 && keys.every(key => /^\d+$/.test(key));
      
      if (allNumericKeys) {
        // Convertir en tableau en respectant l'ordre des clés numériques
        const sortedKeys = keys.map(k => parseInt(k, 10)).sort((a, b) => a - b);
        return sortedKeys.map(key => this.convertObjectsToArrays(obj[key.toString()]));
      }
      
      // Sinon, c'est un objet normal, le laisser tel quel ou le traiter récursivement
      const converted: any = {};
      for (const [key, value] of Object.entries(obj)) {
        converted[key] = this.convertObjectsToArrays(value);
      }
      return converted;
    }
    
    // Valeur primitive
    return obj;
  }

  /**
   * Vérifie si au moins une question audio sera visible selon les règles (visibleIf/hideIf)
   * en utilisant les extProfiles répondues par l'utilisateur.
   * Retourne true si au moins une question audio sera affichée, false sinon.
   */
  private checkAudioQuestionRules(): boolean {
    const publicationAccessService = this.injector.get(PublicationAccessService, null);
    if (!publicationAccessService) {
      console.warn('[ExtProfilesService] PublicationAccessService not available - cannot check audio rules');
      // Si on ne peut pas vérifier les règles, on retourne true par sécurité (comportement actuel)
      return true;
    }

    const publication = publicationAccessService.publication$.value;
    const audioQuestionRules = publication?.generalInfo?.audioQuestionRules || [];
    const hasAudioQuestion = publication?.generalInfo?.hasAudioQuestion ?? false;

    // Si pas de question audio du tout
    if (!hasAudioQuestion) {
      return false;
    }

    // Si hasAudioQuestion est true mais qu'il n'y a pas de règles dans audioQuestionRules,
    // cela signifie qu'il y a au moins une question audio sans règles → elle sera toujours visible
    // NOTE: getAudioRecorderQuestionsWithRules() n'inclut dans audioRules que les questions avec des règles.
    // Donc si hasAudioQuestion est true mais audioQuestionRules est vide, toutes les questions audio n'ont pas de règles.
    if (audioQuestionRules.length === 0) {
      return true;
    }

    // Récupérer les extProfiles de l'utilisateur pour évaluer les règles
    const userExtProfiles = this.userService.user?.extProfiles || {};
    
    // Construire l'objet queryInput avec les extProfiles (format attendu par Query)
    const queryInput = { ...userExtProfiles };

    // IMPORTANT: Si hasAudioQuestion est true mais qu'on a seulement certaines questions dans audioQuestionRules,
    // cela signifie qu'il y a peut-être d'autres questions audio sans règles (qui ne sont pas dans audioQuestionRules).
    // Dans ce cas, au moins une question sera toujours visible → on doit afficher la question ext-profile.
    // Pour éviter ce problème, on ne masque la question ext-profile QUE si TOUTES les questions audio ont des règles
    // ET qu'aucune ne sera visible selon ces règles.
    // 
    // Pour l'instant, on vérifie d'abord si une question dans audioQuestionRules n'a pas de règles.
    // Si c'est le cas, on retourne true immédiatement.
    // Sinon, on vérifie les règles, mais on considère qu'il pourrait y avoir d'autres questions audio sans règles,
    // donc on reste prudent.

    // D'abord, vérifier si une question audio dans la liste n'a pas de règles
    let allQuestionsHaveRules = true;
    for (const audioQuestionRule of audioQuestionRules) {
      const { rules } = audioQuestionRule;

      // Si la question n'a aucune règle (pas de visibleIf, hideIf, stopIf), elle sera toujours visible
      const hasNoRules = !rules || (!rules.hideIf?.finalExpression && !rules.visibleIf?.finalExpression && !rules.stopIf?.finalExpression);
      
      if (hasNoRules) {
        // Si au moins une question n'a pas de règles, elle sera toujours visible → on peut retourner true immédiatement
        return true;
      }
    }

    // Toutes les questions dans audioQuestionRules ont des règles.
    // Mais on ne peut pas être sûr qu'il n'y a pas d'autres questions audio sans règles qui ne sont pas dans audioQuestionRules.
    // Donc pour être sûr, on ne masque la question ext-profile que si on est certain qu'aucune question ne sera visible.
    // Pour l'instant, on vérifie si au moins une question avec règles sera visible.

    // Vérifier si au moins une question avec règles sera visible
    let atLeastOneQuestionWillBeVisible = false;

    for (const audioQuestionRule of audioQuestionRules) {
      const { questionKey, rules } = audioQuestionRule;

      let questionWillBeHidden = false;

      // Vérifier hideIf : si la règle hideIf est vraie, la question est masquée
      if (rules.hideIf?.finalExpression) {
        try {
          // Parser finalExpression si c'est une string JSON (cas où Firestore l'a sérialisé)
          let hideIfExpression = rules.hideIf.finalExpression;
          if (typeof hideIfExpression === 'string') {
            try {
              hideIfExpression = JSON.parse(hideIfExpression);
            } catch (e) {
              console.warn('[ExtProfilesService] Could not parse hideIf.finalExpression as JSON:', e);
            }
          }
          
          // Convertir les objets (stockés par Firebase) en tableaux pour Query
          hideIfExpression = this.convertObjectsToArrays(hideIfExpression);
          
          const hideIfResult: any[] = Query([queryInput])
            .filter(hideIfExpression)
            .toArray() as any[];
          
          // Si hideIf retourne un résultat, la question est masquée
          if (hideIfResult && hideIfResult.length > 0) {
            questionWillBeHidden = true;
            continue; // Passer à la question suivante
          }
        } catch (error) {
          console.error(`[ExtProfilesService] Error evaluating hideIf rule for question ${questionKey}:`, error);
          // En cas d'erreur, on considère que la question pourrait être visible (par sécurité)
        }
      }

      // Vérifier visibleIf : si la règle visibleIf existe, elle doit être vraie pour afficher la question
      if (rules.visibleIf?.finalExpression) {
        try {
          // Parser finalExpression si c'est une string JSON (cas où Firestore l'a sérialisé)
          let visibleIfExpression = rules.visibleIf.finalExpression;
          if (typeof visibleIfExpression === 'string') {
            try {
              visibleIfExpression = JSON.parse(visibleIfExpression);
            } catch (e) {
              console.warn('[ExtProfilesService] Could not parse visibleIf.finalExpression as JSON:', e);
            }
          }
          
          // Convertir les objets (stockés par Firebase) en tableaux pour Query
          visibleIfExpression = this.convertObjectsToArrays(visibleIfExpression);
          
          const visibleIfResult: any[] = Query([queryInput])
            .filter(visibleIfExpression)
            .toArray() as any[];
          
          // Si visibleIf ne retourne pas de résultat, la question n'est pas visible
          if (!visibleIfResult || visibleIfResult.length === 0) {
            continue; // Passer à la question suivante
          }
        } catch (error) {
          console.error(`[ExtProfilesService] Error evaluating visibleIf rule for question ${questionKey}:`, error);
          console.error(`[ExtProfilesService] Error details:`, error.stack);
          // En cas d'erreur, on considère que la question pourrait être visible (par sécurité)
        }
      }

      // Si on arrive ici, la question sera visible (pas de hideIf qui la masque, et visibleIf est vrai ou absent)
      if (!questionWillBeHidden) {
        atLeastOneQuestionWillBeVisible = true;
      }
    }

    // Si on arrive ici, toutes les questions audio ont des règles
    // On retourne true seulement si au moins une sera visible selon les règles
    // Si aucune ne sera visible, on retourne false (et la question ext-profile ne s'affichera pas)
    return atLeastOneQuestionWillBeVisible;
  }

  private verifyMicrophoneAccess(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return Promise.reject(new Error('Microphone API unavailable'));
    }

    return navigator.mediaDevices.getUserMedia({audio: true}).then((stream) => {
      return new Promise<void>((resolve, reject) => {
        const stopStream = () => stream.getTracks().forEach((track) => track.stop());

        if (typeof MediaRecorder === 'undefined') {
          stopStream();
          resolve();
          return;
        }

        let recorder: MediaRecorder;
        try {
          recorder = new MediaRecorder(stream);
        } catch (error) {
          stopStream();
          reject(error);
          return;
        }

        recorder.onerror = (event) => {
          stopStream();
          const err = (event as any)?.error || event;
          reject(err);
        };

        recorder.onstop = () => {
          stopStream();
          resolve();
        };

        recorder.start();

        setTimeout(() => {
          if (recorder.state === 'recording') {
            recorder.stop();
          }
        }, 1500);
      });
    });
  }

}

